import { z } from 'zod'

import {
  MAX_TABS,
  RECENT_LIMIT,
  initialTabs,
  itemKey,
  type TabKey,
  type TabsState,
} from '@/features/chart-tabs/lib/tabsModel'

/**
 * The chart tab strip, kept per device so the chart reopens with the tabs the member left.
 *
 * `localStorage` with every read and write in try/catch, as trading-chart's `lastUsed.ts` does: a
 * blocked or full store costs the remembered tabs, never the chart. Storage is untrusted input, so
 * it is parsed item by item and an entry in an older shape is dropped, not the whole strip.
 *
 * Not cleared at sign-out, for `lastUsed.ts`'s reason: a symbol is not private to a member.
 *
 * Every open chart tab in a browser writes here, and the last write wins: two browser tabs on the
 * chart do not share one strip live, each picks up the other's on its next open.
 */

const TABS_KEY = 'mm-chart-tabs-v1'

const symbolSchema = z
  .string()
  .min(1)
  .max(40)
  .transform((symbol) => symbol.toUpperCase())

const itemSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('chart'), symbol: symbolSchema }),
  z.object({ kind: z.literal('new') }),
])

const keySchema = z.union([
  z.literal('screener'),
  z.literal('new'),
  z.templateLiteral(['chart:', z.string().min(1)]),
])

const listOf = <T>(raw: unknown, schema: z.ZodType<T>): T[] =>
  Array.isArray(raw)
    ? raw.flatMap((entry: unknown) => {
        const parsed = schema.safeParse(entry)
        return parsed.success ? [parsed.data] : []
      })
    : []

/** One tab per key, in stored order, and no more than the strip holds. */
const uniqueItems = (items: TabsState['items']): TabsState['items'] => {
  const seen = new Set<TabKey>()
  return items
    .filter((item) => {
      const key = itemKey(item)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .slice(0, MAX_TABS)
}

/** The stored record, or null when there is none or it cannot be read. */
const readRecord = (): Record<string, unknown> | null => {
  try {
    const raw = window.localStorage.getItem(TABS_KEY)
    const stored: unknown = raw === null ? null : JSON.parse(raw)
    return typeof stored === 'object' && stored !== null
      ? (stored as Record<string, unknown>)
      : null
  } catch {
    return null
  }
}

export const readTabs = (): TabsState => {
  const record = readRecord()
  if (record === null) return initialTabs()
  const active = keySchema.safeParse(record.active)
  return {
    items: uniqueItems(listOf(record.items, itemSchema)),
    active: active.success ? active.data : initialTabs().active,
    recent: [...new Set(listOf(record.recent, symbolSchema))].slice(0, RECENT_LIMIT),
  }
}

export const writeTabs = (state: TabsState): void => {
  try {
    window.localStorage.setItem(TABS_KEY, JSON.stringify(state))
  } catch {
    // Blocked or full storage costs the remembered strip next visit, not the one on screen.
  }
}

/** The tab `/chart` (no symbol) reopens on, when it was not a chart: the screener or New tab. */
export const readActiveView = (): 'screener' | 'new' | undefined => {
  const active = readRecord()?.active
  return active === 'screener' || active === 'new' ? active : undefined
}
